import React, { useState, useMemo } from 'react';
import { Upload, Save, Filter, ChevronLeft, ChevronRight, Download, Trash2, Lock, LogOut } from 'lucide-react';

const ProductManagementApp = () => {
  // Authentication state
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    return sessionStorage.getItem('isAuthenticated') === 'true';
  });
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  // App state
  const [products, setProducts] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [selectedProducts, setSelectedProducts] = useState(new Set<number>());
  const [categoryName, setCategoryName] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [currentView, setCurrentView] = useState('products');
  const [headers, setHeaders] = useState<string[]>([]);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  
  const ITEMS_PER_PAGE = 100;
  const CORRECT_PASSWORD = '7Y*&9QXrSu!B'; // Change this to your desired password

  // Handle login
  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    if (password === CORRECT_PASSWORD) {
      setIsAuthenticated(true);
      sessionStorage.setItem('isAuthenticated', 'true');
      setError('');
    } else {
      setError('Incorrect password. Please try again.');
      setPassword('');
    }
  };

  // Handle logout
  const handleLogout = () => {
    setIsAuthenticated(false);
    sessionStorage.removeItem('isAuthenticated');
    setPassword('');
  };

  // Check if a header is an image column
  const isImageColumn = (header: string) => {
    return header && header.toLowerCase().includes('image');
  };

  // Parse CSV file
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const csv = e.target?.result as string;
      const lines = csv.split('\n').filter(line => line.trim());
      
      if (lines.length === 0) return;

      const headerRow = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));
      setHeaders(headerRow);

      const dataRows = lines.slice(1).map((line, index) => {
        const values = line.split(',').map(v => v.trim().replace(/"/g, ''));
        const product: any = { id: index + 1 };
        headerRow.forEach((header, i) => {
          product[header] = values[i] || '';
        });
        return product;
      });

      setProducts(dataRows);
      setCurrentPage(1);
      setSelectedProducts(new Set());
    };
    reader.readAsText(file);
  };

  const toggleProductSelection = (productId: number) => {
    const newSelected = new Set(selectedProducts);
    if (newSelected.has(productId)) {
      newSelected.delete(productId);
    } else {
      newSelected.add(productId);
    }
    setSelectedProducts(newSelected);
  };

  const toggleSelectAll = () => {
    const currentProducts = getCurrentPageProducts();
    const currentIds = currentProducts.map(p => p.id);
    const allCurrentSelected = currentIds.every(id => selectedProducts.has(id));
    
    const newSelected = new Set(selectedProducts);
    if (allCurrentSelected) {
      currentIds.forEach(id => newSelected.delete(id));
    } else {
      currentIds.forEach(id => newSelected.add(id));
    }
    setSelectedProducts(newSelected);
  };

  const saveToCategory = () => {
    if (!categoryName.trim() || selectedProducts.size === 0) {
      alert('Please enter a category name and select at least one product.');
      return;
    }

    const selectedProductsData = products.filter(p => selectedProducts.has(p.id));
    
    setCategories(prev => {
      const existingCategory = prev.find(cat => cat.name === categoryName);
      if (existingCategory) {
        const existingIds = new Set(existingCategory.products.map((p: any) => p.id));
        const newProducts = selectedProductsData.filter(p => !existingIds.has(p.id));
        return prev.map(cat => 
          cat.name === categoryName 
            ? { ...cat, products: [...cat.products, ...newProducts] }
            : cat
        );
      } else {
        return [...prev, { name: categoryName, products: selectedProductsData }];
      }
    });

    setCategoryName('');
    setSelectedProducts(new Set());
    alert(`Saved ${selectedProducts.size} products to category "${categoryName}"`);
  };

  const getCurrentPageProducts = () => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    return products.slice(startIndex, endIndex);
  };

  const getCategoryProducts = () => {
    if (!selectedCategory) return [];
    const category = categories.find(cat => cat.name === selectedCategory);
    return category ? category.products : [];
  };

  const downloadCategoryCSV = () => {
    if (!selectedCategory || categoryProducts.length === 0) return;
    
    const csvHeaders = headers.join(',');
    const csvRows = categoryProducts.map((product: any) => 
      headers.map(header => {
        const value = product[header] || '';
        const escapedValue = value.toString().replace(/"/g, '""');
        return value.includes(',') || value.includes('"') || value.includes('\n') 
          ? `"${escapedValue}"` 
          : escapedValue;
      }).join(',')
    );
    
    const csvContent = [csvHeaders, ...csvRows].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${selectedCategory.replace(/[^a-z0-9]/gi, '_')}_products.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const clearAllData = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setShowClearConfirm(true);
  };

  const confirmClearAllData = () => {
    setProducts([]);
    setCategories([]);
    setSelectedProducts(new Set());
    setCategoryName('');
    setCurrentPage(1);
    setSelectedCategory('');
    setCurrentView('products');
    setHeaders([]);
    setShowClearConfirm(false);
    
    const fileInputs = document.querySelectorAll('input[type="file"]');
    fileInputs.forEach(input => {
      (input as HTMLInputElement).value = '';
    });
  };

  const cancelClear = () => {
    setShowClearConfirm(false);
  };

  const totalPages = Math.ceil(products.length / ITEMS_PER_PAGE);
  const currentProducts = getCurrentPageProducts();
  const categoryProducts = getCategoryProducts();

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-6">
        <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
              <Lock className="h-8 w-8 text-blue-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Product Selection Tool</h1>
            <p className="text-gray-600">Enter password to access</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-6">
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition"
                placeholder="Enter password"
                autoFocus
              />
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            <button
              type="submit"
              className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition duration-200 shadow-md hover:shadow-lg"
            >
              Sign In
            </button>
          </form>

        </div>
      </div>
    );
  }

  // Main App (after authentication)
  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header with Logout */}
        <div className="flex items-center justify-end mb-4">
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 font-medium shadow-sm"
          >
            <LogOut className="h-4 w-4" />
            Logout
          </button>
        </div>

        {/* Title and Clear Button */}
        <div className="flex items-center justify-between mb-8 pb-6 border-b-2 border-gray-200">
          <h1 className="text-3xl font-bold text-gray-900">Product Selection Tool</h1>
          {products.length > 0 && (
            <button
              type="button"
              onClick={clearAllData}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium shadow-sm"
            >
              <Trash2 className="h-4 w-4" />
              Clear All Data
            </button>
          )}
        </div>

        {showClearConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md mx-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Clear All Data</h3>
              <p className="text-gray-600 mb-6">
                Are you sure you want to clear all products and categories? This action cannot be undone.
              </p>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={cancelClear}
                  className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmClearAllData}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium"
                >
                  Clear All Data
                </button>
              </div>
            </div>
          </div>
        )}
        
        {/* Navigation */}
        <div className="mb-8 flex gap-4 pb-6 border-b border-gray-200">
          <button
            onClick={() => setCurrentView('products')}
            className={`px-4 py-2 rounded-lg font-medium ${
              currentView === 'products'
                ? 'bg-blue-600 text-white'
                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
            }`}
          >
            All Products ({products.length})
          </button>
          <button
            onClick={() => setCurrentView('categories')}
            className={`px-4 py-2 rounded-lg font-medium ${
              currentView === 'categories'
                ? 'bg-blue-600 text-white'
                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
            }`}
          >
            Categories ({categories.length})
          </button>
        </div>

        {/* File Upload */}
        {products.length === 0 && (
          <div className="bg-white rounded-lg shadow-md p-8 mb-6 border border-gray-200">
            <div className="text-center">
              <Upload className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Upload Product CSV</h3>
              <p className="text-gray-600 mb-4">Choose a CSV file with product data. First row should contain headers.</p>
              <input
                type="file"
                accept=".csv"
                onChange={handleFileUpload}
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              />
            </div>
          </div>
        )}

        {/* Products View */}
        {currentView === 'products' && products.length > 0 && (
          <div className="bg-white rounded-lg shadow-md border border-gray-200">
            <div className="p-6 border-b border-gray-200">
              <div className="flex flex-wrap items-center gap-4">
                <button
                  onClick={toggleSelectAll}
                  className="px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100"
                >
                  {getCurrentPageProducts().every(p => selectedProducts.has(p.id)) ? 'Deselect All' : 'Select All'} on Page
                </button>
                
                <span className="text-sm text-gray-600">
                  {selectedProducts.size} selected
                </span>

                {selectedProducts.size > 0 && (
                  <div className="flex items-center gap-2 ml-auto">
                    <input
                      type="text"
                      placeholder="Category name"
                      value={categoryName}
                      onChange={(e) => setCategoryName(e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <button
                      onClick={saveToCategory}
                      className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium"
                    >
                      <Save className="h-4 w-4" />
                      Save to Category
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead className="bg-gray-50">
                  <tr className="border-b-2 border-gray-300">
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-12 border-r border-gray-200">
                      Select
                    </th>
                    {headers.map((header, index) => (
                      <th key={index} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200">
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {currentProducts.map((product) => (
                    <tr key={product.id} className="hover:bg-gray-50 border-b border-gray-200">
                      <td className="px-6 py-4 whitespace-nowrap border-r border-gray-200">
                        <input
                          type="checkbox"
                          checked={selectedProducts.has(product.id)}
                          onChange={() => toggleProductSelection(product.id)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                      </td>
                      {headers.map((header, index) => (
                        <td key={index} className="px-6 py-4 text-sm text-gray-900 border-r border-gray-200">
                          {isImageColumn(header) && product[header] ? (
                            <div className="flex flex-col gap-1">
                              <img
                                src={`https://www.antiquefarmhouse.com/media/catalog/product${product[header]}`}
                                alt="Product"
                                className="h-16 w-16 object-cover rounded border"
                                onError={(e) => {
                                  (e.target as HTMLImageElement).style.display = 'none';
                                  ((e.target as HTMLImageElement).nextSibling as HTMLElement).style.display = 'flex';
                                }}
                              />
                              <div style={{display: 'none'}} className="h-16 w-16 bg-gray-100 rounded border flex flex-col items-center justify-center text-xs text-gray-600 p-1">
                                <div className="text-center">🚫</div>
                                <div className="text-center">CORS Blocked</div>
                                <a 
                                  href={`https://www.antiquefarmhouse.com/media/catalog/product${product[header]}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-500 underline text-[10px] text-center"
                                >
                                  View Image
                                </a>
                              </div>
                              <div className="text-xs text-gray-500 break-all">
                                <div className="font-semibold">Path:</div>
                                <div>{product[header]}</div>
                                <div className="font-semibold mt-1">Full URL:</div>
                                <a 
                                  href={`https://www.antiquefarmhouse.com/media/catalog/product${product[header]}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-600 underline hover:text-blue-800"
                                >
                                  https://www.antiquefarmhouse.com/media/catalog/product{product[header]}
                                </a>
                              </div>
                            </div>
                          ) : isImageColumn(header) ? (
                            <div className="h-16 w-16 bg-gray-200 rounded border flex items-center justify-center text-xs text-gray-500">
                              No Image
                            </div>
                          ) : (
                            product[header]
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                <div className="text-sm text-gray-600">
                  Showing {(currentPage - 1) * ITEMS_PER_PAGE + 1} to {Math.min(currentPage * ITEMS_PER_PAGE, products.length)} of {products.length} products
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="flex items-center px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Previous
                  </button>
                  
                  <span className="px-4 py-2 text-sm font-medium text-gray-700">
                    Page {currentPage} of {totalPages}
                  </span>
                  
                  <button
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className="flex items-center px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {currentView === 'categories' && (
          <div className="space-y-6">
            {categories.length === 0 ? (
              <div className="bg-white rounded-lg shadow-md p-8 text-center border border-gray-200">
                <Filter className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No Categories Yet</h3>
                <p className="text-gray-600">Select products and save them to categories to see them here.</p>
              </div>
            ) : (
              <>
                <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Select Category to View</h3>
                  <div className="flex flex-wrap gap-2">
                    {categories.map((category) => (
                      <button
                        key={category.name}
                        onClick={() => setSelectedCategory(selectedCategory === category.name ? '' : category.name)}
                        className={`px-4 py-2 rounded-lg text-sm font-medium ${
                          selectedCategory === category.name
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {category.name} ({category.products.length})
                      </button>
                    ))}
                  </div>
                </div>

                {selectedCategory && categoryProducts.length > 0 && (
                  <div className="bg-white rounded-lg shadow-md border border-gray-200">
                    <div className="p-6 border-b border-gray-200 flex items-center justify-between">
                      <h3 className="text-lg font-semibold text-gray-900">
                        Products in "{selectedCategory}" ({categoryProducts.length})
                      </h3>
                      <button
                        onClick={downloadCategoryCSV}
                        className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium shadow-sm"
                      >
                        <Download className="h-4 w-4" />
                        Download CSV
                      </button>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full border-collapse">
                        <thead className="bg-gray-50">
                          <tr className="border-b-2 border-gray-300">
                            {headers.map((header, index) => (
                              <th key={index} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200">
                                {header}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {categoryProducts.map((product: any) => (
                            <tr key={product.id} className="hover:bg-gray-50 border-b border-gray-200">
                              {headers.map((header, index) => (
                                <td key={index} className="px-6 py-4 text-sm text-gray-900 border-r border-gray-200">
                                  {isImageColumn(header) && product[header] ? (
                                    <div className="flex flex-col gap-1">
                                      <img
                                        src={`https://www.antiquefarmhouse.com/media/catalog/product${product[header]}`}
                                        alt="Product"
                                        className="h-16 w-16 object-cover rounded border"
                                        onError={(e) => {
                                          (e.target as HTMLImageElement).style.display = 'none';
                                          ((e.target as HTMLImageElement).nextSibling as HTMLElement).style.display = 'flex';
                                        }}
                                      />
                                      <div style={{display: 'none'}} className="h-16 w-16 bg-gray-100 rounded border flex flex-col items-center justify-center text-xs text-gray-600 p-1">
                                        <div className="text-center">🚫</div>
                                        <div className="text-center">CORS Blocked</div>
                                        <a 
                                          href={`https://www.antiquefarmhouse.com/media/catalog/product${product[header]}`}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="text-blue-500 underline text-[10px] text-center"
                                        >
                                          View Image
                                        </a>
                                      </div>
                                      <div className="text-xs text-gray-500 break-all">
                                        <div className="font-semibold">Path:</div>
                                        <div>{product[header]}</div>
                                        <div className="font-semibold mt-1">Full URL:</div>
                                        <a 
                                          href={`https://www.antiquefarmhouse.com/media/catalog/product${product[header]}`}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="text-blue-600 underline hover:text-blue-800"
                                        >
                                          https://www.antiquefarmhouse.com/media/catalog/product{product[header]}
                                        </a>
                                      </div>
                                    </div>
                                  ) : isImageColumn(header) ? (
                                    <div className="h-16 w-16 bg-gray-200 rounded border flex items-center justify-center text-xs text-gray-500">
                                      No Image
                                    </div>
                                  ) : (
                                    product[header]
                                  )}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductManagementApp;
